import { EarningType } from "../../../__generated__/graphql";
import { GetMissionVacationStatusQuery } from "../../../__generated__/operations/graphql";
import { MissionPayrollBreakdownFieldsFragment } from "../../../__generated__/operations/graphql";
import React, { useCallback, useMemo } from "react";
import { useI18n } from "../../../hooks/i18n-hooks";

import VStack from "../../../ui_atoms/VStack";
import { daysAsString, minutesAsString } from "../../../utils";
import LabelValueText from "../../LabelValueText";
import { useTheme } from "../../../hooks/use-theme";

type PayrollBreakdownTotalInfoProps = {
    payrollBreakdown: MissionPayrollBreakdownFieldsFragment;
    vacationStatus?: GetMissionVacationStatusQuery["getMissionVacationStatus"];
    disabled?: boolean;
};

const PayrollBreakdownTotalInfo: React.FC<PayrollBreakdownTotalInfoProps> = ({
    payrollBreakdown,
    vacationStatus,
    disabled = false,
}) => {
    const i18n = useI18n();
    const hoursPerDay = useMemo(() => {
        return parseFloat(vacationStatus?.contractHoursPerWeekFullTime || "0") / 5;
    }, [vacationStatus]);

    const vacationInDays = useMemo(() => {
        const minutes = payrollBreakdown.usedVacationMinutes || 0;
        const days = minutes / 60 / hoursPerDay;
        return days >= 1 || payrollBreakdown.missionInfo.earningType === EarningType.Monthly;
    }, [
        hoursPerDay,
        payrollBreakdown.usedVacationMinutes,
        payrollBreakdown.missionInfo.earningType,
    ]);

    const vacationAsString = useCallback(() => {
        if (disabled) {
            return "--";
        }
        if (hoursPerDay) {
            const minutes = payrollBreakdown.usedVacationMinutes || 0;
            if (vacationInDays) {
                const days = minutes / 60 / hoursPerDay;
                return daysAsString(days);
            } else {
                return minutesAsString(minutes);
            }
        }
    }, [hoursPerDay, payrollBreakdown.usedVacationMinutes, vacationInDays, disabled]);
    const { fonts } = useTheme();

    const tableData = useMemo(
        () =>
            [
                {
                    title: i18n.t(
                        vacationInDays
                            ? "payroll_breakdown.field_total_vacation_days"
                            : "payroll_breakdown.field_total_vacation_hours",
                    ),
                    value: disabled ? "--" : vacationAsString(),
                },
                {
                    title: i18n.t("payroll_breakdown.field_total_work_days"),
                    value: disabled
                        ? "--"
                        : payrollBreakdown.totalWorkedDays !== null
                          ? daysAsString(Number(payrollBreakdown.totalWorkedDays))
                          : undefined,
                },
            ].filter((x) => x.value != null),
        [i18n, vacationInDays, disabled, vacationAsString, payrollBreakdown.totalWorkedDays],
    );

    return (
        <VStack style={{ opacity: disabled ? 0.3 : 1.0 }} gap={0.25}>
            {tableData.map((item) => (
                <LabelValueText
                    key={item.title}
                    label={item.title}
                    value={item.value || ""}
                    valueStyle={fonts.bodyLarge}
                />
            ))}
        </VStack>
    );
};

export default PayrollBreakdownTotalInfo;
